import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { IconExternalLink, IconPlug } from '@tabler/icons-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import useTranslation from '@/hooks/useTranslation';
import CoLoopProjectMapping from './CoLoopProjectMapping';
import { useGetOrgIntegrations, useGetProjectIntegration } from '@/reactQuery/integrations';

/**
 * Right-aligned action on the ProjectResults page. Sits next to
 * DownloadAnalysisButton and opens a modal with the full CoLoop
 * mapping + push flow.
 *
 * Only rendered when the current org has SOME coloop record
 * (`connected` / `expired` / `revoked`). Orgs that never
 * connected the integration don't see it — the discovery path
 * is org Settings → Integrations, not a persistent header
 * button on every project.
 */
function CoLoopSendButton({ disabled }: { disabled?: boolean }) {
  const { lang } = useTranslation();
  const { organization_id: organizationId, project_id: projectId } = useParams();
  const [open, setOpen] = useState(false);

  const orgIntegrations = useGetOrgIntegrations(organizationId);
  const coloop = orgIntegrations.data?.find((i) => i.provider === 'coloop');
  const projectMapping = useGetProjectIntegration(projectId);

  // Label reflects the state the user is about to enter when clicking:
  //  - connected + linked → Push all
  //  - connected + not linked yet → Send to CoLoop (opens the picker)
  //  - expired / revoked → CoLoop (reconnect) — sets expectation before click
  let label = lang.get('msg.integrationsColoopSendLabel');
  if (coloop?.status === 'connected' && projectMapping.data) {
    label = lang.get('msg.integrationsColoopPushAll');
  } else if (coloop?.status === 'expired' || coloop?.status === 'revoked') {
    label = lang.get('msg.integrationsColoopReconnectLabel');
  }

  if (!organizationId || !projectId) return null;
  // Hide entirely for orgs that never wired up CoLoop. `useGetOrgIntegrations`
  // returns [] while loading and while the org genuinely has none — both
  // read as "not for this org" here, which is the correct UX (don't flash
  // a button then remove it once the query settles). Once a record exists
  // in ANY status (expired/revoked count too — user needs the reconnect
  // affordance) we render.
  if (!coloop) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <DialogTrigger asChild>
              {/*
               * Icon-only to match the adjacent DownloadAnalysisButton
               * shape (h-9 w-9). The old text-forward variant read as
               * a primary CTA in the header; the actual "push" action
               * lives inside the dialog. Tooltip carries the state
               * label so no context is lost.
               */}
              <Button
                type="button"
                variant="outline"
                size="icon"
                disabled={disabled}
                aria-label={label}
                className="h-9 w-9 shrink-0"
              >
                <IconPlug className="h-4 w-4" />
              </Button>
            </DialogTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom">{label}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <DialogContent className="max-w-2xl">
        <DialogHeader className="flex flex-row items-center justify-between gap-3">
          <DialogTitle>CoLoop</DialogTitle>
          {/*
           * Escape hatch to the full org-level integration settings —
           * connect, disconnect, view scopes. The dialog itself is
           * scoped to this project's mapping; anything cross-project
           * lives on the settings page.
           */}
          <Button asChild variant="ghost" size="sm" className="mr-6 text-xs">
            <Link to={`/organization/${organizationId}/integrations`}>
              {lang.get('msg.integrationsColoopManage')}
              <IconExternalLink className="ml-1.5 h-3 w-3" />
            </Link>
          </Button>
        </DialogHeader>
        <CoLoopProjectMapping organizationId={organizationId} projectId={projectId} />
      </DialogContent>
    </Dialog>
  );
}

export default CoLoopSendButton;
